import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { DataFhirAdapter, DataFhirCategorySetting } from '../data.model';
import { UserService } from '../../services/user.service';
import { DictionariesCollectionService } from '../dictionaries/dictionaries-collection.service';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CategoryElement } from '../partial/categories.model';
import { QuestionnaireEntry } from './questionnaire-entry.model';

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireEntryAdapter extends DataFhirAdapter<QuestionnaireEntry> {
  categorySettings: DataFhirCategorySetting<QuestionnaireEntry>[] = [
    {
      categoryName: 'ageGroup',
      dictionaryIds: ['taxonomytermagegroup'],
    },
    {
      categoryName: 'partner',
      dictionaryIds: ['taxonomytermpartner'],
    },
    {
      categoryName: 'clinicalPicture',
      dictionaryIds: ['taxonomytermclinicalpicture'],
    },
    {
      categoryName: 'symptoms',
      dictionaryIds: ['taxonomytermsymptom'],
    },
    {
      categoryName: 'subjects',
      dictionaryIds: ['taxonomytermsubject'],
    },
    {
      categoryName: 'audience',
      dictionaryIds: ['taxonomytermaudience'],
    },
  ];

  constructor(
    protected dictionariesCollection: DictionariesCollectionService,
    private userService: UserService,
  ) {
    super('ServiceRequest', dictionariesCollection);
  }

  protected blankResource(): any {
    return {
      type: 'questionnaire',
      name: '',
      releasedBy: [this.userService.getUserReference()],
      releasedOn: formatDate(new Date(), 'yyyy-MM-ddTHH:mm:ss', 'en'),
    };
  }

  fromResource$(resource, bundle?): Observable<QuestionnaireEntry> {
    return super.fromResource$(resource).pipe(
      switchMap((baseItem) => {
        const questionnaireId =
          resource.supportingInfo?.[0]?.reference?.split('/')?.[1];
        const practitionerId = resource.requester?.reference?.split('/')?.[1];
        const practitioners = bundle?.entry?.filter(
          (entry) => entry?.resource?.resourceType === 'Practitioner',
        );
        const practitioner = practitioners?.find(
          (item) => item.resource?.id === practitionerId,
        );
        const releasedBy = practitioner?.resource?.['name'][0].text;

        const questionnaireModel = {
          ...resource,
          ...baseItem,
          questionnaireId,
          name: bundle?.title || resource.name,
          description: bundle?.description || resource.description,
          status: resource.status,
          releasedOn: resource.authoredOn,
          lastUpdated: resource?.meta.lastUpdated,
          questionnaireVersion: resource?.version || bundle?.meta?.versionId,
          patientId: resource.subject?.reference?.split('/')?.[1],
          //needed for sorting
          type: 'questionnaire',
          patientOnly: !!this.isPatientOnlyCoding(resource?.meta?.security),
        };

        if (releasedBy) questionnaireModel['releasedBy'] = releasedBy;

        return this.getCategories$(bundle).pipe(
          map((categories) => {
            questionnaireModel['classification'] = [
              categories.map((category) => category.name),
            ];

            return questionnaireModel;
          }),
        );
      }),
    );
  }

  getCategories$(detail): Observable<CategoryElement[]> {
    const codes = detail?.useContext?.map((item) => item?.code.code) || [];

    return forkJoin(
      this.categorySettings.map(({ categoryName }) =>
        this.getCategoryOptionsList$(categoryName).pipe(take(1)),
      ),
    ).pipe(
      map((lists: CategoryElement[][]) => lists.flat()),
      map((options) => options.filter((option) => codes.includes(option.id))),
    );
  }

  toResource(questionnaireEntry: QuestionnaireEntry): fhir4.ServiceRequest {
    const baseResource = super.toResource(
      questionnaireEntry,
    ) as fhir4.ServiceRequest;

    return {
      ...baseResource,
      status: questionnaireEntry.status,
    };
  }
}
